import { forwardRef, FocusEvent } from "react";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { CheckboxProps } from "./Checkbox";
import { Label } from "../shared";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Check } from "./Check";
import { useValidity } from "../ValidityGroup";
import styles from "./styles.module.scss";
import { SharedCheckboxPlaceholder } from "./SharedCheckboxPlaceholder";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `SharedCheckbox` component properties.
 */
export interface SharedCheckboxProps
  extends Omit<CheckboxProps, "instructions" | "hideLabel" | "hideInstructions" | "onFocusEnter" | "onFocusExit">,
    Pick<AccessibilityProps, "aria-label">
   {
  /**
   * The input ID.
   */
  readonly id: string;

  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;

  /**
   * Function to call when the checkbox is focused.
   */
  readonly onFocus?: (ev: FocusEvent) => void;

  /**
   * Function to call when the checkbox is unfocused.
   */
  readonly onBlur?: (ev: FocusEvent) => void;
}

/**
 * The shared checkbox component used by the `Checkbox`, `Checkbox.Field` and `CheckboxGroup` components.
 */
export const SharedCheckbox = forwardRef<HTMLInputElement, SharedCheckboxProps>(function SharedCheckbox(props, ref) {
  const { id, required = false, label, tabIndex, autoFocus, defaultValue = null, onChange, onFocus, onBlur, tooltip, unpadded, "aria-label": ariaLabel } = props;
  const { value = defaultValue } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const [invalid] = useValidity();
  const submitting = useSubmitting();

  const checked = value === true;
  const indeterminate = value === "indeterminate";

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.SharedCheckbox,
      "shared-checkbox",
      {
        indeterminate,
        checked,
        invalid,
        disabled,
        readonly: readOnly || submitting,
        unpadded,
      },
    ],
    testIdPrefix: "shared-checkbox",
  });

  return (
    <LoadingGroup.Placeholder fallback={<SharedCheckboxPlaceholder className={props.className} style={props.style} />}>
      <Body {...getBasePropsFromHtmlProps(baseHtmlProps)}>
        <Shelf spacing="none" alignment="top">
          <Check
            id={id}
            ref={ref}
            tabIndex={tabIndex}
            autoFocus={autoFocus}
            value={value}
            defaultValue={defaultValue}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            aria-label={ariaLabel}
          />
          {/* disabled this rule in this file because Label
            is managing the association with the checkbox through forId prop
            but eslint can't detect that
          */}
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <Label forId={id} required={required} tooltip={tooltip} size="small" appearance="regular">
            {label}
          </Label>
          <Shelf.Spacer />
        </Shelf>
      </Body>
    </LoadingGroup.Placeholder>
  );
});
